import PropTypes from 'prop-types';
import { isForbidden } from 'js/api/WebRequest';

export interface ApiCallAction<Payload = unknown> {
  type: string;
  payload?: Payload;
}

export interface ApiCallState<Data, Params = unknown> {
  data: Data;
  pending: boolean;
  forbidden: boolean;
  error: unknown;
  params: Params | undefined;
}

export interface IncrementalRequestParams<Seek = unknown> {
  seek?: Seek | null;
  [key: string]: unknown;
}

export interface IncrementalDataPayload<Data, Seek = unknown> {
  data: Data[];
  next_seek: Seek | null;
  at_start: boolean;
  at_end: boolean;
  [key: string]: unknown;
}

export interface IncrementalApiCallState<
  Data,
  Seek = unknown,
  Params extends
    IncrementalRequestParams<Seek> = IncrementalRequestParams<Seek>,
> extends ApiCallState<Data[], Params> {
  next_seek: Seek | null;
  at_start: boolean;
  at_end: boolean;
}

export type ApiCallReducer<Data, Params = unknown> = (
  state: ApiCallState<Data, Params> | undefined,
  action: ApiCallAction
) => ApiCallState<Data, Params>;

export type IncrementalApiCallReducer<
  Data,
  Seek = unknown,
  Params extends
    IncrementalRequestParams<Seek> = IncrementalRequestParams<Seek>,
> = (
  state: IncrementalApiCallState<Data, Seek, Params> | undefined,
  action: ApiCallAction
) => IncrementalApiCallState<Data, Seek, Params>;

export function fulfilled(actionType: string): string {
  return actionType + '_FULFILLED';
}

export function pending(actionType: string): string {
  return actionType + '_PENDING';
}

export function rejected(actionType: string): string {
  return actionType + '_REJECTED';
}

export function clearResults(actionType: string): string {
  return actionType + '_CLEAR';
}

export function setData(actionType: string): string {
  return actionType + '_SET_DATA';
}

function createReducer<Data, Params = unknown>(
  actionType: string,
  emptyData: Data,
  clear = true
): ApiCallReducer<Data, Params> {
  const initial_state: ApiCallState<Data, Params> = {
    data: emptyData,
    pending: false,
    forbidden: false,
    error: null,
    params: undefined,
  };
  const FULFILLED = fulfilled(actionType);
  const REJECTED = rejected(actionType);
  const CLEAR = clearResults(actionType);
  const SET_DATA = setData(actionType);

  function reducer(
    state: ApiCallState<Data, Params> = initial_state,
    action: ApiCallAction
  ): ApiCallState<Data, Params> {
    switch (action.type) {
      case actionType: {
        const s = clear ? initial_state : state;
        return { ...s, pending: true, params: action.payload as Params };
      }
      case FULFILLED: {
        return {
          ...initial_state,
          params: state.params,
          data: action.payload as Data,
        };
      }
      case REJECTED: {
        return {
          ...initial_state,
          params: state.params,
          error: action.payload,
          forbidden: isForbidden(action.payload),
        };
      }
      case CLEAR: {
        return { ...initial_state };
      }
      case SET_DATA: {
        return { ...initial_state, data: action.payload as Data };
      }
      default:
        return state;
    }
  }

  return reducer;
}

export function createSingleDataReducer<Data = unknown, Params = unknown>(
  actionType: string,
  clear = true,
  initalState: Data | null = null
): ApiCallReducer<Data | null, Params> {
  return createReducer<Data | null, Params>(actionType, initalState, clear);
}

export function createArrayDataReducer<Data = unknown, Params = unknown>(
  actionType: string,
  clear = true
): ApiCallReducer<Data[], Params> {
  return createReducer<Data[], Params>(actionType, [], clear);
}

export function createIncrementalDataReducer<
  Data = unknown,
  Seek = unknown,
  Params extends
    IncrementalRequestParams<Seek> = IncrementalRequestParams<Seek>,
>(actionType: string): IncrementalApiCallReducer<Data, Seek, Params> {
  const initial_state: IncrementalApiCallState<Data, Seek, Params> = {
    data: [],
    next_seek: null,
    at_start: true,
    at_end: true,
    pending: false,
    forbidden: false,
    error: null,
    params: undefined,
  };
  const FULFILLED = fulfilled(actionType);
  const REJECTED = rejected(actionType);
  const CLEAR = clearResults(actionType);

  function reducer(
    state: IncrementalApiCallState<Data, Seek, Params> = initial_state,
    action: ApiCallAction
  ): IncrementalApiCallState<Data, Seek, Params> {
    switch (action.type) {
      case actionType: {
        const payload = action.payload as Params;
        if (!payload.seek) {
          return { ...initial_state, params: payload, pending: true };
        } else {
          return { ...state, at_start: false, pending: true };
        }
      }
      case FULFILLED: {
        const payload = action.payload as IncrementalDataPayload<Data, Seek>;
        if (Boolean(state.at_start) !== Boolean(payload.at_start)) {
          // ignore unexpected data (initial vs. incremental request)
          return state;
        }
        return {
          ...initial_state,
          ...payload,
          params: state.params,
          data: state.at_start
            ? payload.data
            : [...state.data, ...payload.data],
        };
      }
      case REJECTED: {
        return {
          ...initial_state,
          params: state.params,
          error: action.payload,
          forbidden: isForbidden(action.payload),
        };
      }
      case CLEAR: {
        return { ...initial_state };
      }
      default:
        return state;
    }
  }

  return reducer;
}

export const singleDataType = PropTypes.shape({
  data: PropTypes.object,
  pending: PropTypes.bool,
  error: PropTypes.string,
});

export const arrayDataType = PropTypes.shape({
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  pending: PropTypes.bool,
  error: PropTypes.string,
});

export const incrementalDataType = PropTypes.shape({
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  next_seek: PropTypes.object,
  at_start: PropTypes.bool,
  at_end: PropTypes.bool,
  pending: PropTypes.bool,
  error: PropTypes.string,
});
